import React, { useEffect } from 'react';
import { useLanguage } from './contexts/LanguageContext';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from './components/Layout';
import NotionLayout from './components/NotionLayout';
import useIdleLogout from './hooks/useIdleLogout';
// Pages
import Login from './components/LoginForm';
import Register from './components/RegisterFormSinglePage';
// 8 oct 2026: demo público del sitio web -- ver pages/EntrarDemo.js.
//
// OJO: existen dos routes.js (este, src/routes.js, y
// src/components/routes.js). El que App.js importa es ESTE; el otro quedó
// de una refactorización vieja y editarlo no tiene ningún efecto.
import EntrarDemo from './pages/EntrarDemo';
import UserProfileSetup from './components/UserProfileSetup';
import NotionDashboard from './components/NotionDashboard';
import Transactions from './pages/TransactionsPage';
import Budgets from './components/Budgets';
import CuentasPage from './pages/CuentasPage';
import Debts from './pages/DebtsPage';
import RecurringPage from './pages/RecurringPage';
import CalendarPage from './pages/CalendarPage';
import Reports from './components/Reports';
import Subscription from './components/Subscription';
import VoiceCommands from './components/VoiceCommands';
import WelcomePage from './components/WelcomePage';
import RevisionPage from './pages/RevisionPage';
import ConexionesPage from './pages/ConexionesPage';
import ConciliacionPage from './pages/ConciliacionPage';
import OnboardingIntro from './components/OnboardingIntro';
import ForgotPasswordForm from './components/ForgotPasswordForm';
import ResetPasswordForm from './components/ResetPasswordForm';
import ThankYouPage from './components/ThankYouPage';
import HomePage from './components/HomePage';
import ConsultantPage from './components/ConsultantPage';
import FeaturesPage from './components/FeaturesPage';
import PreciosPage from './components/PreciosPage';
import NosotrosPage from './components/NosotrosPage';
import TermsAndConditionsPage from './components/TermsAndConditionsPage';
import UserProfile from './components/UserProfile';
import DeleteUserPage from './pages/DeleteUserPage';
import ClientesPage from './pages/ClientesPage';
// 3 sep 2026 (Augusto: "no, lo quiero como otra sección dentro de
// ADMIN"): segunda página bajo la sección ADMIN del sidebar -- ver
// NotionSidebar.js y TestersPage.js.
import TestersPage from './pages/TestersPage';
// 17 sep 2026: panel de la agenda propia de consultas (reemplaza Cal.com).
import ConsultasPage from './pages/ConsultasPage';
// 25 ago 2026 (Task #10 -- pedido de Augusto: "Crea un lugar aparte para
// Categorias, donde se puedan modificar y corregir"): nueva página para
// ver/renombrar/fusionar/eliminar categorías -- ver CategoriesPage.js.
import CategoriesPage from './pages/CategoriesPage';
// Resource pages
import GuidesPage from './components/GuidesPage';
import SupportPage from './components/SupportPage';
// Private Route component
const PrivateRoute = ({ children }) => {
  const isAuthenticated = useSelector((state) => state.auth.token);
  const location = useLocation();
  // Check both Redux state and sessionStorage for authentication (3 ago
  // 2026: sessionStorage en vez de localStorage, para que cerrar el
  // navegador cierre también la sesión -- ver services/api.js).
  const localStorageToken = sessionStorage.getItem('token');
  const isLocallyAuthenticated = !!localStorageToken;
  console.log('Auth check:', { reduxToken: isAuthenticated, localToken: localStorageToken });
  // Si no está autenticado ni en Redux ni en localStorage, redirigir a login
  if (!isAuthenticated && !isLocallyAuthenticated) {
    console.log('User not authenticated, redirecting to login');
    return <Navigate to="/login?from=portal" replace />;
  }
  // For dashboard specifically, make sure to mark onboarding as completed
  // to prevent redirect loops
  if (location.pathname === '/dashboard') {
    localStorage.setItem('onboardingCompleted', 'true');
  }
  // If authenticated only in localStorage but not in Redux, sync the state
  if (!isAuthenticated && isLocallyAuthenticated) {
    console.log('Syncing auth state from localStorage to Redux');
    // This would typically dispatch an action to set the token in Redux
    // But for simplicity we'll rely on the localStorage token
  }
  return children;
};
// Define which routes should use the public marketing layout vs app layout
// Nota: '/terminos-y-condiciones' está acá para que use el mismo layout
// público (header/footer del sitio) y sea accesible sin haber iniciado
// sesión, pero es una página "oculta" a propósito — no tiene ningún link
// desde PublicHeader, PublicFooter ni el menú de la app. Solo se llega a
// ella escribiendo la URL directamente (o enlazándola puntualmente donde
// haga falta, ej. checkbox de registro).
const publicRoutes = ['/', '/home', '/consultant', '/features', '/precios', '/nosotros', '/login', '/register', '/forgot-password', '/reset-password', '/terminos-y-condiciones'];
// Routes that should use a completely clean layout (no headers, footers, or sidebars)
// 10 ago 2026: /onboarding/intro (pantalla de bienvenida post-registro,
// ver OnboardingIntro.js) vive acá a propósito -- es una pantalla de un
// solo paso ("Comenzar") sin necesidad del sidebar de onboarding
// (OnboardingSidebar todavía no tiene nada que mostrar en este punto) ni
// del menú completo.
const cleanRoutes = ['/onboarding/intro'];
// Routes that should use the new Notion style layout
// /welcome vive aca ahora: es "Inicio" en el sidebar, la pantalla de
// bienvenida/resumen que ve el cliente al entrar a trabajar (antes era una
// ruta "limpia" sin sidebar, separada del resto de la app).
// 12 ago 2026: /guides, /blog y /support se movieron acá desde
// publicRoutes -- antes vivían en el sitio de marketing (con el header
// público, Recursos en el dropdown), ahora son parte del menú de la app
// (ver NotionSidebar.js, sección Recursos) y usan el mismo layout con
// sidebar que el resto. GuidesPage.js/SupportPage.js ya no se envuelven
// en <Layout> ellos mismos -- lo hace este wrapper, igual que Reports.js
// y el resto.
// 25 ago 2026: /categorias se agrega acá con el mismo patrón que
// /subscription y /voice -- una sub-página de Configuración, con su
// propio breadcrumb (ver el switch más abajo).
// 3 sep 2026 (pedido de Augusto: "quita la sección Blog"): /blog se saca
// de acá -- BlogPage.js nunca tuvo contenido real (solo "En
// construcción"), ver también NotionSidebar.js.
// 3 sep 2026: /testers se agrega con el mismo patrón que /clientes --
// segunda página bajo la sección ADMIN del sidebar (ver
// NotionSidebar.js, TestersPage.js).
// 17 sep 2026: toda ruta privada nueva TIENE que sumarse a esta lista.
// Lo que no está acá cae al `Layout` legacy del final de RouteWrapper, que
// dibuja un menú viejo y roto (siete ítems que dicen literalmente "title",
// porque llama a t() con los argumentos al revés -- ver components/Layout.js).
// Pasó al agregar /consultas: la ruta existía y la página cargaba bien, pero
// alrededor salía ese menú. Si agregas una pantalla y el menú se ve raro,
// esto es lo primero que hay que revisar.
const notionStyleRoutes = ['/welcome', '/dashboard', '/transactions', '/revision', '/conciliacion', '/conexiones', '/accounts', '/budgets', '/debts', '/recurring', '/calendar', '/reports', '/subscription', '/voice', '/settings', '/profile', '/clientes', '/testers', '/consultas', '/categorias', '/guides', '/support'];
// Wrapper to conditionally apply the appropriate Layout
const RouteWrapper = ({ children }) => {
  const location = useLocation();
  const { language, t } = useLanguage();
  // Cierre de sesión automático a los 15 min de inactividad — no hace
  // nada mientras no haya token (usuario en páginas públicas o deslogueado).
  useIdleLogout();
  // 4 ago 2026 (reporte de Augusto: "cuando hago clic en hacer la prueba,
  // me manda a la parte inferior de la pantalla"): React Router no
  // resetea el scroll al cambiar de ruta -- si el usuario estaba
  // scrolleado hacia abajo en una página larga (ej. Precios) y navega a
  // una más corta (ej. Registro), el navegador conserva esa misma
  // posición de scroll, que ahora cae cerca del fondo (o más allá) de la
  // página nueva. Este efecto fuerza scroll al tope en cada cambio de
  // ruta, que es el comportamiento esperado de cualquier sitio normal.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const isPublicRoute = publicRoutes.includes(location.pathname);
  const isNotionStyleRoute = notionStyleRoutes.some(route => location.pathname.startsWith(route));
  const isCleanRoute = cleanRoutes.includes(location.pathname);
  // If it's a clean route, return children directly without any layout
  if (isCleanRoute) {
    return <>{children}</>;
  }
  // If it's a public route, use public layout with language selector and modern navigation
  if (isPublicRoute) {
    return <Layout isPublic={true}>{children}</Layout>;
  }
  // If it's a route that should use the Notion style layout
  if (isNotionStyleRoute) {
    // Determine title and breadcrumbs based on route
    let title = '';
    let breadcrumbs = [];
    switch(true) {
      case location.pathname.startsWith('/welcome'):
        title = language === 'es' ? 'Inicio' : 'Home';
        breadcrumbs = [{ label: language === 'es' ? 'Inicio' : 'Home', path: null }];
        break;
      case location.pathname === '/dashboard':
        title = language === 'es' ? 'Inicio' : 'Dashboard';
        breadcrumbs = [{ label: language === 'es' ? 'Bienvenido' : 'Welcome', path: '/welcome' }, { label: language === 'es' ? 'Inicio' : 'Dashboard', path: null }];
        break;
      case location.pathname === '/conexiones':
        breadcrumbs = [{ label: language === 'es' ? 'Conexiones' : 'Connections', path: null }];
        break;
      case location.pathname === '/conciliacion':
        title = language === 'es' ? 'Conciliación' : 'Reconciliation';
        breadcrumbs = [{ label: language === 'es' ? 'Cuentas' : 'Accounts', path: '/accounts' }, { label: language === 'es' ? 'Conciliación' : 'Reconciliation', path: null }];
        break;
      case location.pathname === '/revision':
        title = language === 'es' ? 'Revisión pendiente' : 'Pending review';
        breadcrumbs = [{ label: language === 'es' ? 'Transacciones' : 'Transactions', path: '/transactions' }, { label: language === 'es' ? 'Revisión pendiente' : 'Pending review', path: null }];
        break;
      case location.pathname === '/transactions':
        title = language === 'es' ? 'Transacciones' : 'Transactions';
        breadcrumbs = [{ label: language === 'es' ? 'Bienvenido' : 'Welcome', path: '/welcome' }, { label: language === 'es' ? 'Transacciones' : 'Transactions', path: null }];
        break;
      case location.pathname === '/accounts':
        title = language === 'es' ? 'Cuentas' : 'Accounts';
        breadcrumbs = [{ label: language === 'es' ? 'Bienvenido' : 'Welcome', path: '/welcome' }, { label: language === 'es' ? 'Cuentas' : 'Accounts', path: null }];
        break;
      case location.pathname === '/budgets':
        title = language === 'es' ? 'Presupuestos' : 'Budgets';
        breadcrumbs = [{ label: language === 'es' ? 'Bienvenido' : 'Welcome', path: '/welcome' }, { label: language === 'es' ? 'Presupuestos' : 'Budgets', path: null }];
        break;
      case location.pathname === '/debts':
        title = language === 'es' ? 'Deudas' : 'Debts';
        breadcrumbs = [{ label: language === 'es' ? 'Bienvenido' : 'Welcome', path: '/welcome' }, { label: language === 'es' ? 'Deudas' : 'Debts', path: null }];
        break;
      case location.pathname === '/recurring':
        title = language === 'es' ? 'Recurrentes' : 'Recurring';
        breadcrumbs = [{ label: language === 'es' ? 'Bienvenido' : 'Welcome', path: '/welcome' }, { label: language === 'es' ? 'Recurrentes' : 'Recurring', path: null }];
        break;
      case location.pathname === '/calendar':
        title = language === 'es' ? 'Calendario' : 'Calendar';
        breadcrumbs = [{ label: language === 'es' ? 'Bienvenido' : 'Welcome', path: '/welcome' }, { label: language === 'es' ? 'Calendario' : 'Calendar', path: null }];
        break;
      case location.pathname === '/reports':
        title = language === 'es' ? 'Informes' : 'Reports';
        breadcrumbs = [{ label: language === 'es' ? 'Bienvenido' : 'Welcome', path: '/welcome' }, { label: language === 'es' ? 'Informes' : 'Reports', path: null }];
        break;
      case location.pathname === '/subscription':
        title = language === 'es' ? 'Suscripción' : 'Subscription';
        breadcrumbs = [{ label: language === 'es' ? 'Configuración' : 'Settings', path: '/settings' }, { label: language === 'es' ? 'Suscripción' : 'Subscription', path: null }];
        break;
      case location.pathname === '/voice':
        title = language === 'es' ? 'Comandos de Voz' : 'Voice Commands';
        breadcrumbs = [{ label: language === 'es' ? 'Configuración' : 'Settings', path: '/settings' }, { label: language === 'es' ? 'Comandos de Voz' : 'Voice Commands', path: null }];
        break;
      case location.pathname === '/categorias':
        title = language === 'es' ? 'Categorías' : 'Categories';
        breadcrumbs = [{ label: language === 'es' ? 'Configuración' : 'Settings', path: '/settings' }, { label: language === 'es' ? 'Categorías' : 'Categories', path: null }];
        break;
      case location.pathname === '/settings':
        title = language === 'es' ? 'Configuración' : 'Settings';
        breadcrumbs = [{ label: language === 'es' ? 'Configuración' : 'Settings', path: null }];
        break;
      case location.pathname === '/profile':
        title = language === 'es' ? 'Perfil' : 'Profile';
        breadcrumbs = [{ label: language === 'es' ? 'Configuración' : 'Settings', path: '/settings' }, { label: language === 'es' ? 'Perfil' : 'Profile', path: null }];
        break;
      case location.pathname === '/clientes':
        title = language === 'es' ? 'Consumo IA Docs/Clientes' : 'AI Usage/Client';
        breadcrumbs = [{ label: language === 'es' ? 'Consumo IA Docs/Clientes' : 'AI Usage/Client', path: null }];
        break;
      case location.pathname === '/testers':
        title = language === 'es' ? 'Cuentas de testing' : 'Testing accounts';
        breadcrumbs = [{ label: 'ADMIN', path: null }, { label: language === 'es' ? 'Cuentas de testing' : 'Testing accounts', path: null }];
        break;
      case location.pathname === '/consultas':
        title = language === 'es' ? 'Consultas agendadas' : 'Scheduled consultations';
        breadcrumbs = [{ label: 'ADMIN', path: null }, { label: language === 'es' ? 'Consultas agendadas' : 'Scheduled consultations', path: null }];
        break;
      case location.pathname === '/guides':
        title = language === 'es' ? 'Guías' : 'Guides';
        breadcrumbs = [{ label: language === 'es' ? 'Recursos' : 'Resources', path: null }, { label: language === 'es' ? 'Guías' : 'Guides', path: null }];
        break;
      case location.pathname === '/support':
        title = language === 'es' ? 'Soporte' : 'Support';
        breadcrumbs = [{ label: language === 'es' ? 'Recursos' : 'Resources', path: null }, { label: language === 'es' ? 'Soporte' : 'Support', path: null }];
        break;
      default:
        title = language === 'es' ? 'Administrador de Finanzas' : 'Finance Manager';
        breadcrumbs = [];
    }
    return <NotionLayout title={title} breadcrumbs={breadcrumbs}>{children}</NotionLayout>;
  }
  // For other private/authenticated routes, use app layout with sidebar
  return <Layout isPublic={false}>{children}</Layout>;
};
const RoutesComponent = () => {
  return (
    <RouteWrapper>
      <Routes>
        {/* Public marketing pages */}
        <Route path="/" element={<HomePage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/consultant" element={<ConsultantPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/precios" element={<PreciosPage />} />
        <Route path="/nosotros" element={<NosotrosPage />} />
        <Route path="/terminos-y-condiciones" element={<TermsAndConditionsPage />} />
        {/* Resource pages */}
        <Route path="/guides" element={<GuidesPage />} />
        <Route path="/support" element={<SupportPage />} />
        {/* Authentication routes - only keeping login with portal parameter */}
        <Route path="/login" element={<Login />} />
        {/* Redirect all other auth routes to the portal login */}
        <Route path="/register" element={<Register />} />
        {/* 8 oct 2026: demo público. Pública a propósito (fuera de
            PrivateRoute): su trabajo es crear la sesión de demostración y
            mandar al tablero. */}
        <Route path="/demo-en-vivo" element={<EntrarDemo />} />
        <Route path="/profile-setup" element={<UserProfileSetup />} />
        <Route path="/thank-you" element={<Navigate to="/welcome" replace />} />
        <Route path="/onboarding/intro" element={<PrivateRoute><OnboardingIntro /></PrivateRoute>} />
        <Route path="/welcome" element={<PrivateRoute><WelcomePage /></PrivateRoute>} />
        <Route path="/welcome/accounts" element={<PrivateRoute><WelcomePage section="accounts" /></PrivateRoute>} />
        <Route path="/welcome/debts" element={<PrivateRoute><WelcomePage section="debts" /></PrivateRoute>} />
        <Route path="/welcome/incomes" element={<PrivateRoute><WelcomePage section="incomes" /></PrivateRoute>} />
        <Route path="/welcome/expenses" element={<PrivateRoute><WelcomePage section="expenses" /></PrivateRoute>} />
        <Route path="/welcome/summary" element={<PrivateRoute><WelcomePage section="summary" /></PrivateRoute>} />
        <Route path="/forgot-password" element={<ForgotPasswordForm />} />
        <Route path="/reset-password" element={<ResetPasswordForm />} />
        {/* Protected routes */}
        <Route path="/dashboard" element={<PrivateRoute><NotionDashboard /></PrivateRoute>} />
        {/* 21 sep 2026: los movimientos importados que esperan una
            decisión -- ver RevisionPage.js. */}
        <Route path="/revision" element={<PrivateRoute><RevisionPage /></PrivateRoute>} />
        {/* 24 sep 2026 (Augusto: "PCM debe ayudar a reconciliar las
            cuentas... y dejar el aplicativo listo para seguir ingresando
            data") -- ver ConciliacionPage.js. */}
        <Route path="/conciliacion" element={<PrivateRoute><ConciliacionPage /></PrivateRoute>} />
        <Route path="/conexiones" element={<PrivateRoute><ConexionesPage /></PrivateRoute>} />
        <Route path="/transactions" element={<PrivateRoute><Transactions /></PrivateRoute>} />
        <Route path="/accounts" element={<CuentasPage />} />
        <Route path="/budgets" element={<PrivateRoute><Budgets /></PrivateRoute>} />
        <Route path="/debts" element={<PrivateRoute><Debts /></PrivateRoute>} />
        <Route path="/recurring" element={<PrivateRoute><RecurringPage /></PrivateRoute>} />
        <Route path="/calendar" element={<PrivateRoute><CalendarPage /></PrivateRoute>} />
        <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
        <Route path="/subscription" element={<PrivateRoute><Subscription /></PrivateRoute>} />
        <Route path="/voice" element={<PrivateRoute><VoiceCommands /></PrivateRoute>} />
        {/* 25 ago 2026 (Task #10): página de gestión de categorías. */}
        <Route path="/categorias" element={<PrivateRoute><CategoriesPage /></PrivateRoute>} />
        <Route path="/profile" element={<UserProfile />} />
        <Route path="/clientes" element={<PrivateRoute><ClientesPage /></PrivateRoute>} />
        <Route path="/testers" element={<PrivateRoute><TestersPage /></PrivateRoute>} />
        <Route path="/consultas" element={<PrivateRoute><ConsultasPage /></PrivateRoute>} />
        <Route path="/delete-user" element={<DeleteUserPage />} />
        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </RouteWrapper>
  );
};
export default RoutesComponent;
